import React from 'react';
import { Box, Typography, Divider, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// =====================================================================
// MarkdownView — hiển thị nội dung Markdown (đã bật GFM: bảng, checklist, gạch ngang).
//
// Dùng ở: trang tuỳ biến của gói mở rộng (sandbox cấp sẵn biến `Markdown`) và bất cứ chỗ nào cần
// render tài liệu dài.
//
// VÌ SAO CÓ FILE NÀY: `react-markdown` mặc định nhả ra thẻ HTML trần — bảng không viền, code không
// nền, tiêu đề không phân cấp. Tài liệu hướng dẫn dài vài trăm dòng render như vậy thì không ai đọc
// nổi. Ở đây ánh xạ từng thẻ sang component MUI để thừa hưởng theme của ứng dụng (kể cả nền tối).
//
//   <MarkdownView>{noiDungMarkdown}</MarkdownView>
// =====================================================================

/** Bỏ dấu tiếng Việt -> id neo ổn định cho tiêu đề (mục lục nhảy tới được). */
export const slugify = (s) => String(s == null ? '' : s)
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/đ/g, 'd').replace(/Đ/g, 'D')
  .toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/** Gom text của một node markdown về chuỗi (để làm id neo). */
const textOf = (children) => React.Children.toArray(children)
  .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : (c && c.props ? textOf(c.props.children) : '')))
  .join('');

const H = (variant, sx) => {
  const Heading = ({ children }) => {
    const t = textOf(children);
    return <Typography id={slugify(t)} variant={variant} sx={{ scrollMarginTop: 16, ...sx }}>{children}</Typography>;
  };
  return Heading;
};

const components = {
  h1: H('h5', { fontWeight: 800, mt: 3, mb: 1, lineHeight: 1.25 }),
  h2: H('h6', { fontWeight: 700, mt: 3, mb: 1, lineHeight: 1.3 }),
  h3: H('subtitle1', { fontWeight: 700, mt: 2, mb: 0.75 }),
  h4: H('subtitle2', { fontWeight: 700, mt: 1.5, mb: 0.5 }),
  p: ({ children }) => <Typography variant="body2" sx={{ my: 1, lineHeight: 1.75 }}>{children}</Typography>,
  a: ({ href, children }) => {
    const inApp = typeof href === 'string' && href.startsWith('#/');
    return <Link href={href} target={inApp ? undefined : '_blank'} rel={inApp ? undefined : 'noopener noreferrer'} sx={{ fontWeight: 500 }}>{children}</Link>;
  },
  ul: ({ children }) => <Box component="ul" sx={{ pl: 2.5, my: 1, '& li': { mb: 0.4 } }}>{children}</Box>,
  ol: ({ children }) => <Box component="ol" sx={{ pl: 2.5, my: 1, '& li': { mb: 0.4 } }}>{children}</Box>,
  li: ({ children }) => <Box component="li" sx={{ typography: 'body2', lineHeight: 1.7 }}>{children}</Box>,
  hr: () => <Divider sx={{ my: 3 }} />,
  // Trích dẫn trong tài liệu hầu như luôn là cảnh báo/lưu ý -> cho nó nổi lên như Alert.
  blockquote: ({ children }) => (
    <Alert severity="info" icon={false} sx={{ my: 1.5, borderLeft: 3, borderColor: 'info.main', borderRadius: 1, '& p': { my: 0.25 } }}>
      {children}
    </Alert>
  ),
  code: ({ inline, children }) => inline
    ? <Box component="code" sx={{ px: 0.6, py: 0.15, borderRadius: 0.75, bgcolor: 'action.hover', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.84em', wordBreak: 'break-word' }}>{children}</Box>
    : <Box component="code" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem', lineHeight: 1.6 }}>{children}</Box>,
  pre: ({ children }) => (
    <Box component="pre" sx={{ my: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider', overflowX: 'auto', '& code': { bgcolor: 'transparent', p: 0 } }}>
      {children}
    </Box>
  ),
  // Bảng phải cuộn ngang được — tài liệu hay có bảng 4-5 cột, màn hình hẹp là vỡ bố cục.
  table: ({ children }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 1.5, borderRadius: 1 }}>
      <Table size="small" sx={{ minWidth: 420 }}>{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover', whiteSpace: 'nowrap' } }}>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableCell sx={{ fontSize: '0.78rem' }}>{children}</TableCell>,
  td: ({ children }) => <TableCell sx={{ fontSize: '0.82rem', verticalAlign: 'top' }}>{children}</TableCell>,
  img: ({ src, alt }) => <Box component="img" src={src} alt={alt || ''} sx={{ maxWidth: '100%', borderRadius: 1, my: 1 }} />,
};

export default function MarkdownView({ children, sx }) {
  return (
    <Box sx={{ '& > :first-of-type': { mt: 0 }, ...sx }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {typeof children === 'string' ? children : ''}
      </ReactMarkdown>
    </Box>
  );
}
